<template>
  <div class="platform-admin-layout" :class="{ 'dark-theme': isDarkTheme }">
    <!-- Header -->
    <header class="platform-header">
      <div class="header-content">
        <div class="logo-section">
          <router-link to="/" class="logo-link">
            <img src="@/assets/omind_logo.png" alt="OMIND" class="logo" />
          </router-link>
          <div class="header-divider"></div>
          <h1 class="header-title">
            <i class="pi pi-shield"></i>
            Platform Admin
          </h1>
          <span class="env-badge" :class="'env-badge--' + envInfo.color">
            {{ envInfo.label }}
          </span>
        </div>
        <div class="header-actions">
          <button
            @click="toggleTheme"
            class="theme-toggle"
            :title="isDarkTheme ? 'Switch to Light' : 'Switch to Dark'"
          >
            <i :class="isDarkTheme ? 'pi pi-sun' : 'pi pi-moon'"></i>
          </button>
          <span class="user-badge">
            <i class="pi pi-user"></i>
            {{ userEmail }}
          </span>
          <button @click="logout" class="logout-btn">
            <i class="pi pi-sign-out"></i>
            Logout
          </button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <div class="main-container">
      <PlatformSidebar :isDarkTheme="isDarkTheme" />
      <main class="content-area">
        <router-view :isDarkTheme="isDarkTheme" />
      </main>
    </div>
  </div>
</template>

<script>
import PlatformSidebar from './PlatformSidebar.vue';
import { getEnvironmentInfo } from '@/helpers/environmentLabel';

export default {
  name: 'PlatformLayout',
  components: {
    PlatformSidebar,
  },
  data() {
    return {
      isDarkTheme: false,
      envInfo: getEnvironmentInfo(),
    };
  },
  computed: {
    platformUser() {
      const userStr = localStorage.getItem('platform_user');
      if (userStr) {
        try {
          return JSON.parse(userStr);
        } catch (e) {
          return null;
        }
      }
      return null;
    },
    userEmail() {
      return this.platformUser?.email || 'Admin';
    },
    userName() {
      return this.platformUser?.name || 'Admin';
    },
    userRole() {
      return this.platformUser?.role || 'platform_admin';
    },
  },
  mounted() {
    // Load theme preference from localStorage
    const savedTheme = localStorage.getItem('platform_theme');
    this.isDarkTheme = savedTheme === 'dark';
    this.updateBodyTheme();
  },
  watch: {
    isDarkTheme() {
      this.updateBodyTheme();
    },
  },
  methods: {
    updateBodyTheme() {
      // Apply dark theme to body so dialogs inherit it
      // Use both .dark-theme (for custom CSS) and .p-dark (for PrimeVue)
      if (this.isDarkTheme) {
        document.body.classList.add('dark-theme', 'p-dark');
      } else {
        document.body.classList.remove('dark-theme', 'p-dark');
      }
    },
    toggleTheme() {
      this.isDarkTheme = !this.isDarkTheme;
      localStorage.setItem('platform_theme', this.isDarkTheme ? 'dark' : 'light');
      this.updateBodyTheme();
    },
    logout() {
      // Clear platform tokens
      localStorage.removeItem('platform_token');
      localStorage.removeItem('platform_refresh_token');
      localStorage.removeItem('platform_user');

      // Redirect to platform login
      this.$router.push('/platform/login');
    },
  },
};
</script>

<style scoped>
/* ========== Light Theme (Default) ========== */
.platform-admin-layout {
  --bg-primary: #f8f8f8;
  --bg-secondary: #ffffff;
  --bg-tertiary: #f4f4f4;
  --border-color: #e7e7e7;
  --text-primary: #1E2019;
  --text-secondary: #666666;
  --text-muted: #999999;
  --accent-bg: rgba(228, 130, 26, 0.1);
  --accent-border: rgba(228, 130, 26, 0.2);
  --shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  --shadow-hover: 0 4px 12px rgba(0, 0, 0, 0.08);

  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
  font-family: 'AsenPro', sans-serif;
  transition: all 0.3s ease;
}

/* ========== Dark Theme ========== */
.platform-admin-layout.dark-theme {
  --bg-primary: #1E2019;
  --bg-secondary: #2a2a24;
  --bg-tertiary: #353530;
  --border-color: rgba(228, 130, 26, 0.2);
  --text-primary: #f5f5f5;
  --text-secondary: #a8a8a0;
  --text-muted: #777777;
  --accent-bg: rgba(228, 130, 26, 0.15);
  --accent-border: rgba(228, 130, 26, 0.3);
  --shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  --shadow-hover: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.platform-header {
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  box-shadow: var(--shadow);
  position: sticky;
  top: 0;
  z-index: 100;
  transition: all 0.3s ease;
}

.header-content {
  max-width: 1920px;
  margin: 0 auto;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.logo-link {
  display: flex;
  align-items: center;
}

.logo {
  height: 40px;
  width: auto;
}

.header-divider {
  width: 1px;
  height: 30px;
  background: linear-gradient(180deg, transparent, rgba(228, 130, 26, 0.3), transparent);
}

.header-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-title i {
  color: var(--color-orange, #e4821a);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.theme-toggle {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-bg);
  border: 1px solid var(--accent-border);
  border-radius: 50%;
  color: #e4821a;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.theme-toggle:hover {
  transform: rotate(15deg);
  box-shadow: 0 4px 15px rgba(228, 130, 26, 0.3);
}

.user-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--accent-bg);
  border: 1px solid var(--accent-border);
  border-radius: 9999px;
  color: var(--text-primary);
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.logout-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid rgba(228, 75, 85, 0.3);
  border-radius: 0.5rem;
  color: #e44b55;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.logout-btn:hover {
  background: rgba(228, 75, 85, 0.1);
  border-color: #e44b55;
}

.main-container {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.content-area {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  background: var(--bg-primary);
  transition: all 0.3s ease;
}

.env-badge {
  display: inline-block;
  padding: 0.2rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  line-height: 1.4;
}

.env-badge--red {
  background: rgba(220, 38, 38, 0.15);
  color: #dc2626;
  border: 1px solid rgba(220, 38, 38, 0.4);
}

.env-badge--amber {
  background: rgba(217, 119, 6, 0.15);
  color: #d97706;
  border: 1px solid rgba(217, 119, 6, 0.4);
}

.env-badge--green {
  background: rgba(22, 163, 74, 0.15);
  color: #16a34a;
  border: 1px solid rgba(22, 163, 74, 0.4);
}
</style>
